import { requestReload } from './reloadGate';

/**
 * Base API client with common functionality for both admin and app frontends.
 * Supports both HttpOnly cookie-based auth (preferred) and localStorage fallback.
 * Extend this class to add specific API methods.
 */

/** Refresh the access token this many seconds before it expires (keeps long uploads alive). */
const PROACTIVE_REFRESH_SECONDS = 600; // 10 minutes
/** Tolerance when judging whether a token is effectively expired. */
const EXPIRY_SKEW_SECONDS = 30;
/** Abort a refresh request after this long so a hung network can't freeze callers. */
const REFRESH_TIMEOUT_MS = 10_000;

/**
 * Thrown for every 401. `message` is the server's `error` field when the body carries one
 * ("Invalid credentials", "Please check your email and set your password first", ...) and the
 * fixed string "Unauthorized" otherwise, so callers that must tell "session gone" from any
 * other failure check `instanceof UnauthorizedError` rather than the message text.
 */
export class UnauthorizedError extends Error {
  readonly status = 401;
  /** Machine-readable code from the body, e.g. `PASSWORD_NOT_SET`. */
  readonly code?: string;
  /** Login attempts left before the account locks (password login only). */
  readonly remainingAttempts?: number;

  constructor(body: unknown) {
    const data = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
    super(typeof data.error === 'string' && data.error ? data.error : 'Unauthorized');
    this.name = 'UnauthorizedError';
    if (typeof data.code === 'string') this.code = data.code;
    if (typeof data.remainingAttempts === 'number') this.remainingAttempts = data.remainingAttempts;
  }
}

export abstract class BaseApiClient {
  protected token: string | null = null;
  protected apiUrl: string;
  protected storageKey: string;
  /** Endpoint that exchanges the refresh cookie for a fresh access token, or null if the
   *  client has no refresh flow (e.g. admin). Set by subclasses. */
  protected refreshPath: string | null;
  /** De-dupes concurrent refreshes: many near-expiry requests share one network call. */
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(apiUrl: string, storageKey: string, refreshPath?: string) {
    this.apiUrl = apiUrl;
    this.storageKey = storageKey;
    this.refreshPath = refreshPath ?? null;
    this.token = this.loadToken();
  }

  private loadToken(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(this.storageKey);
    }
    return null;
  }

  /**
   * Set the auth token. If token is provided, it's stored in localStorage
   * as a fallback mechanism. The preferred auth method is HttpOnly cookies
   * which are set by the server on login.
   */
  setToken(token: string | null): void {
    this.token = token;
    if (typeof localStorage !== 'undefined') {
      if (token) {
        localStorage.setItem(this.storageKey, token);
      } else {
        localStorage.removeItem(this.storageKey);
      }
    }
  }

  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if user is authenticated.
   * Note: With HttpOnly cookies, we may not have a token in memory but still
   * be authenticated via cookies. This method checks for localStorage token
   * as a quick check; actual auth status is determined by API responses.
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Clear local auth state and localStorage.
   * Called on logout or when receiving 401 responses.
   */
  clearAuth(): void {
    this.token = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.storageKey);
    }
  }

  // ---------------------------------------------------------------------------
  // Token-expiry awareness + silent refresh (sliding session)
  // ---------------------------------------------------------------------------

  /** Decode a JWT's `exp` claim (seconds since epoch) → ms, or null if not decodable. */
  protected decodeExpiryMs(token: string | null): number | null {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json) as { exp?: number };
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** True if there is no token, or the token expires within `seconds` from now. */
  isExpiringWithin(seconds: number): boolean {
    const expMs = this.decodeExpiryMs(this.token);
    if (expMs === null) return true; // unknown/undecodable → treat as needs-refresh
    return expMs - Date.now() <= seconds * 1000;
  }

  /**
   * Exchange the HttpOnly refresh cookie for a fresh access token. Best-effort and
   * de-duplicated: concurrent callers await a single in-flight request. Returns true
   * only if a new token was obtained. No-op (false) when the client has no refresh flow.
   */
  async refreshSession(): Promise<boolean> {
    if (!this.refreshPath) return false;
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
      try {
        const res = await fetch(`${this.apiUrl}${this.refreshPath}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) return false;
        const data = (await res.json().catch(() => null)) as { token?: string } | null;
        if (data && typeof data.token === 'string') {
          this.setToken(data.token);
          return true;
        }
        return false;
      } catch {
        // Network error or timeout (abort): treat as a failed refresh.
        return false;
      } finally {
        clearTimeout(timer);
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /**
   * Ensure a usable access token exists before starting a long operation (e.g. an upload):
   * if the current token is healthy, return true; otherwise attempt a silent refresh and
   * report whether we now hold a non-expired token. False means the caller should redirect
   * to sign-in without starting the operation.
   */
  async ensureValidToken(): Promise<boolean> {
    // Healthy token, comfortably before expiry → good to go.
    if (this.token && !this.isExpiringWithin(PROACTIVE_REFRESH_SECONDS)) return true;
    // Token is missing or near expiry. When a refresh flow exists, its result is decisive:
    // a failed refresh (401 after logout/password-change/expired refresh, or a timeout) means
    // the session can't be renewed, so the caller must NOT start the operation. Returning the
    // stale-but-not-yet-expired token here would let an upload begin on a dead session.
    if (this.refreshPath) {
      return this.refreshSession();
    }
    // No refresh flow (e.g. admin): valid iff the token isn't effectively expired.
    return !!this.token && !this.isExpiringWithin(EXPIRY_SKEW_SECONDS);
  }

  /**
   * Clear the session and send the user to sign-in. The reload is routed through the
   * reload gate, so a redirect triggered mid-upload is deferred until the upload finishes.
   */
  expireSession(reason: string = 'session_expired'): void {
    this.clearAuth();
    this.redirectToSignIn(reason);
  }

  /** Stash a reason for the login screen, then reload (deferred while uploads run). */
  protected redirectToSignIn(reason: string): void {
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem('post_logout_reason', reason);
      } catch {
        // ignore storage failures (private mode, etc.)
      }
    }
    requestReload(reason);
  }

  protected async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    // Proactively refresh a soon-to-expire token so long operations (bulk uploads) never
    // die mid-flight. Best-effort: a failed refresh falls through to normal 401 handling.
    if (this.refreshPath && this.token && this.isExpiringWithin(PROACTIVE_REFRESH_SECONDS)) {
      await this.refreshSession();
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Include Authorization header if we have a token in memory/localStorage
    // This provides backward compatibility and supports API clients
    // The server will also check for HttpOnly cookies
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.apiUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // Include credentials (cookies) with every request
      // This enables HttpOnly cookie-based authentication
      credentials: 'include',
    });

    if (response.status === 401) {
      // Keep the server's reason: a login 401 says *why* ("Invalid credentials" with the
      // attempts left, "set your password first") and the UI must show it. Body parsing is
      // best-effort — a 401 without a JSON body still throws, with the generic message.
      const body: unknown = await response.json().catch(() => null);
      this.clearAuth();
      this.onUnauthorized();
      throw new UnauthorizedError(body);
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  /**
   * Called when a 401 response is received.
   * Override in subclass for custom behavior (e.g. to suppress during a login attempt).
   */
  protected onUnauthorized(): void {
    this.redirectToSignIn('session_expired');
  }
}
